import { useEffect, useState } from "react";
import { Public, Subscriptions, Wallet, Me, getAuth,
  type SubscriptionRule, type NotifyChannel, type SubscriptionConfig } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { confirmDialog, customDialog, toast } from "@/components/ui/dialog";
import { Bell, Mail, Plus, Trash2, ShieldCheck, AlertTriangle, RotateCcw } from "lucide-react";
import { Link } from "@/lib/router";

const FIELD_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "title",         label: "标题" },
  { key: "summary",       label: "摘要" },
  { key: "content_text",  label: "正文" },
  { key: "bid_sections",  label: "标段清单" },
  { key: "bidder",        label: "招标方" },
  { key: "winner",        label: "中标方" },
  { key: "winners",       label: "中标方列表" },
  { key: "province",      label: "省份" },
  { key: "year",          label: "年份" },
  { key: "pur_type",      label: "采购方式" },
  { key: "type",          label: "公告类型" },
  { key: "category",      label: "细分类目" },
  { key: "packages",      label: "包件" },
];

export function SubscriptionsTab() {
  const [cfg, setCfg] = useState<SubscriptionConfig | null>(null);
  const [rules, setRules] = useState<SubscriptionRule[]>([]);
  const [channels, setChannels] = useState<NotifyChannel[]>([]);
  const [cats, setCats] = useState<any[]>([]);
  const [wallet, setWallet] = useState<{ points: number } | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = () => Promise.all([
    Subscriptions.config(), Subscriptions.rules(), Subscriptions.channels(),
    Wallet.get().catch(() => null),
  ]).then(([c, r, ch, w]) => {
    setCfg(c); setRules(r); setChannels(ch); setWallet(w);
  });

  useEffect(() => {
    Public.categories().then(setCats).catch(() => {});
    reload().finally(() => setLoading(false));
  }, []);

  if (loading || !cfg) return <div className="text-sm text-muted-foreground">加载中...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2"><Bell className="h-4 w-4" />订阅推送</CardTitle>
          <div className="text-xs text-muted-foreground">
            每次推送扣除 <b className="text-foreground">{cfg.points_per_push}</b> 积分 · 钱包余额 <b className="text-foreground">{wallet?.points ?? "—"}</b>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {!cfg.enabled && <NoticeBar tone="warning" text="订阅功能当前未开放。" />}
          {cfg.enabled && cfg.require_active_vip && !cfg.active_vip && (
            <NoticeBar tone="warning"
              text={<>仅 VIP 用户可创建订阅规则。<Link to="/pricing" className="underline">前往升级 VIP</Link></>} />
          )}
          {cfg.enabled && cfg.active_vip && (
            <NoticeBar tone="info" text={
              <>当前你拥有有效 VIP，可创建最多 <b>{cfg.max_rules_per_user}</b> 条订阅规则（已用 {cfg.rule_count}）。</>
            } />
          )}
        </CardContent>
      </Card>

      {/* 通知通道 */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2"><Mail className="h-4 w-4" />通知通道</CardTitle>
          <Button size="sm" onClick={() => addChannel(reload)}>
            <Plus className="h-4 w-4 mr-1" /> 添加邮箱
          </Button>
        </CardHeader>
        <CardContent>
          {channels.length === 0
            ? <div className="text-sm text-muted-foreground">还没有通道。添加邮箱后才能接收推送。</div>
            : <div className="space-y-2">
                {channels.map(ch => <ChannelRow key={ch.id} ch={ch} onChange={reload} />)}
              </div>}
        </CardContent>
      </Card>

      {/* 订阅规则 */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>订阅规则</CardTitle>
          <Button size="sm" disabled={!cfg.can_subscribe} onClick={() => editRule(null, cats, channels, reload)}>
            <Plus className="h-4 w-4 mr-1" /> 新建规则
          </Button>
        </CardHeader>
        <CardContent>
          {rules.length === 0
            ? <div className="text-sm text-muted-foreground">还没有订阅规则。</div>
            : <div className="space-y-3">
                {rules.map(r => (
                  <RuleCard key={r.id} rule={r} cats={cats} channels={channels} onChange={reload} />
                ))}
              </div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">最近推送</CardTitle></CardHeader>
        <CardContent><OutboxList /></CardContent>
      </Card>
    </div>
  );
}

function NoticeBar({ tone, text }: { tone: "info" | "warning"; text: React.ReactNode }) {
  const cls = tone === "warning"
    ? "bg-amber-50 text-amber-800 ring-amber-200"
    : "bg-sky-50 text-sky-800 ring-sky-200";
  const Icon = tone === "warning" ? AlertTriangle : ShieldCheck;
  return (
    <div className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ring-1 ${cls}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" /><div>{text}</div>
    </div>
  );
}

function ChannelRow({ ch, onChange }: { ch: NotifyChannel; onChange: () => void }) {
  const verify = async () => {
    try {
      await Subscriptions.requestChannelVerify(ch.id);
      const code = await customDialog<string>((close) => (
        <PromptCode title="输入邮箱验证码" target={ch.target} onSubmit={(v) => close(v)} onCancel={() => close(null)} />
      ));
      if (!code) return;
      await Subscriptions.confirmChannelVerify(ch.id, code);
      toast.success("验证成功");
      onChange();
    } catch (e: any) { toast.error(e?.body?.message || e?.message || "操作失败"); }
  };
  const del = async () => {
    if (!await confirmDialog({ title: "删除通道", message: `确认删除「${ch.target}」？关联的订阅规则将失效。` })) return;
    await Subscriptions.deleteChannel(ch.id);
    toast.success("已删除"); onChange();
  };
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <span className="truncate">{ch.target}</span>
        {ch.verified_at
          ? <Badge variant="secondary" className="text-emerald-700 bg-emerald-50">已验证</Badge>
          : <Badge variant="secondary" className="text-amber-700 bg-amber-50">未验证</Badge>}
      </div>
      <div className="flex items-center gap-1">
        {!ch.verified_at && <Button size="sm" variant="outline" onClick={verify}>验证</Button>}
        <Button size="sm" variant="ghost" onClick={del}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function RuleCard({ rule, cats, channels, onChange }: {
  rule: SubscriptionRule; cats: any[]; channels: NotifyChannel[]; onChange: () => void;
}) {
  const toggle = async () => {
    try {
      await Subscriptions.updateRule(rule.id, { ...rule, is_active: !rule.is_active });
      onChange();
    } catch (e: any) { toast.error(e?.body?.message || e?.message || "操作失败"); }
  };
  const del = async () => {
    if (!await confirmDialog({ title: "删除规则", message: `确认删除规则「${rule.name || rule.keyword}」？` })) return;
    await Subscriptions.deleteRule(rule.id);
    toast.success("已删除"); onChange();
  };
  return (
    <div className="rounded-md border p-3 space-y-2 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium truncate">{rule.name || rule.keyword}</div>
          <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-2">
            <span>关键词：<b className="text-foreground">{rule.keyword}</b></span>
            <span>栏目：{rule.category_name || "全部"}</span>
            <span>命中：{rule.hit_count}</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {rule.match_fields.map(f => (
              <Badge key={f} variant="outline" className="text-[10px] py-0 h-5">
                {FIELD_OPTIONS.find(o => o.key === f)?.label || f}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Badge variant={rule.is_active ? "default" : "secondary"}>{rule.is_active ? "启用" : "停用"}</Badge>
          <Button size="sm" variant="outline" onClick={toggle}>{rule.is_active ? "停用" : "启用"}</Button>
          <Button size="sm" variant="outline" onClick={() => editRule(rule, cats, channels, onChange)}>编辑</Button>
          <Button size="sm" variant="ghost" onClick={del}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}

async function addChannel(reload: () => void) {
  const target = await customDialog<string>((close) => (
    <PromptCode title="添加邮箱通道" subtitle="将向该邮箱发送验证码完成绑定" target=""
      onSubmit={(v) => close(v)} onCancel={() => close(null)} />
  ));
  if (!target) return;
  try {
    const r = await Subscriptions.createChannel("email", target.trim());
    if (r.needs_verify) {
      await Subscriptions.requestChannelVerify(r.id);
      const code = await customDialog<string>((close) => (
        <PromptCode title="输入邮箱验证码" target={target.trim()} onSubmit={(v) => close(v)} onCancel={() => close(null)} />
      ));
      if (code) {
        await Subscriptions.confirmChannelVerify(r.id, code);
        toast.success("绑定成功");
      } else {
        toast.info("已添加但未验证；可在列表点「验证」");
      }
    } else {
      toast.success("已添加");
    }
    reload();
  } catch (e: any) {
    toast.error(e?.body?.message || e?.message || "添加失败");
  }
}

async function editRule(
  rule: SubscriptionRule | null, cats: any[], channels: NotifyChannel[], reload: () => void
) {
  const result = await customDialog<{
    name: string; keyword: string; category_id: string | null;
    match_fields: string[]; channel_ids: string[]; is_active: boolean;
  }>((close) => (
    <RuleForm rule={rule} cats={cats} channels={channels}
      onSubmit={(v) => close(v)} onCancel={() => close(null)} />
  ));
  if (!result) return;
  try {
    if (rule) await Subscriptions.updateRule(rule.id, result as any);
    else await Subscriptions.createRule(result as any);
    toast.success(rule ? "已更新" : "已创建");
    reload();
  } catch (e: any) {
    toast.error(e?.body?.message || e?.message || "保存失败");
  }
}

function PromptCode({ title, subtitle, target, onSubmit, onCancel }: {
  title: string; subtitle?: string; target: string;
  onSubmit: (v: string) => void; onCancel: () => void;
}) {
  const [val, setVal] = useState(target || "");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-lg bg-background p-5 shadow-xl">
        <h3 className="font-semibold">{title}</h3>
        {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
        {target && target !== val && <p className="mt-2 text-xs text-muted-foreground">目标：{target}</p>}
        <Input className="mt-3" value={val} onChange={(e) => setVal(e.target.value)} autoFocus />
        <div className="mt-4 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}>取消</Button>
          <Button size="sm" onClick={() => onSubmit(val.trim())} disabled={!val.trim()}>确定</Button>
        </div>
      </div>
    </div>
  );
}

function RuleForm({ rule, cats, channels, onSubmit, onCancel }: {
  rule: SubscriptionRule | null; cats: any[]; channels: NotifyChannel[];
  onSubmit: (v: any) => void; onCancel: () => void;
}) {
  const [name, setName] = useState(rule?.name || "");
  const [keyword, setKeyword] = useState(rule?.keyword || "");
  const [categoryId, setCategoryId] = useState<string | "">(rule?.category_id || "");
  const [fields, setFields] = useState<string[]>(rule?.match_fields || ["title", "bid_sections", "bidder", "winner"]);
  const [chs, setChs] = useState<string[]>(rule?.channel_ids || []);
  const [active, setActive] = useState<boolean>(rule?.is_active ?? true);
  const toggle = (arr: string[], v: string) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg bg-background p-5 shadow-xl">
        <h3 className="font-semibold">{rule ? "编辑订阅规则" : "新建订阅规则"}</h3>
        <div className="mt-4 grid gap-3 text-sm">
          <div><Label>名称</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="如：变压器中标提醒" /></div>
          <div><Label>关键词 <span className="text-rose-500">*</span></Label>
            <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="必填，模糊匹配" /></div>
          <div><Label>限定栏目（可选）</Label>
            <select className="w-full mt-1 h-9 rounded-md border bg-background px-2 text-sm"
                    value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">全部栏目</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><Label>匹配字段</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {FIELD_OPTIONS.map(f => {
                const on = fields.includes(f.key);
                return (
                  <button key={f.key} type="button" onClick={() => setFields(toggle(fields, f.key))}
                    className={`text-xs px-2.5 py-1 rounded-full ring-1 ${on
                      ? "bg-primary text-primary-foreground ring-primary"
                      : "bg-background text-muted-foreground ring-border hover:bg-muted"}`}>
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div><Label>通知通道（不选则使用全部已激活通道）</Label>
            <div className="mt-1 grid gap-1">
              {channels.length === 0
                ? <div className="text-xs text-muted-foreground">先添加邮箱通道</div>
                : channels.map(c => (
                    <label key={c.id} className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={chs.includes(c.id)}
                        onChange={() => setChs(toggle(chs, c.id))} />
                      <span>{c.target}</span>
                      {!c.verified_at && <Badge variant="secondary" className="text-amber-700 bg-amber-50">未验证</Badge>}
                    </label>
                  ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            启用
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}>取消</Button>
          <Button size="sm" disabled={!keyword.trim() || fields.length === 0}
            onClick={() => onSubmit({
              name, keyword: keyword.trim(),
              category_id: categoryId || null,
              match_fields: fields, channel_ids: chs, is_active: active,
            })}>保存</Button>
        </div>
      </div>
    </div>
  );
}

function OutboxList() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const reload = () => Subscriptions.outbox(1, 10).then(r => setItems(r.items)).finally(() => setLoading(false));
  useEffect(() => {
    reload();
    if (getAuth()) Me.profile().then(p => setIsAdmin(p?.roles?.includes("admin"))).catch(() => {});
  }, []);
  const retry = async (id: number) => {
    try {
      await Subscriptions.adminRetryOutbox(id);
      toast.success("已加入重试队列");
      reload();
    } catch (e: any) {
      toast.error(e?.body?.message || e?.message || "重试失败");
    }
  };
  if (loading) return <div className="text-xs text-muted-foreground">加载中...</div>;
  if (items.length === 0) return <div className="text-xs text-muted-foreground">还没有推送记录。</div>;
  return (
    <div className="space-y-1.5 text-xs">
      {items.map(it => (
        <div key={it.id} className="flex items-start justify-between gap-2 rounded-md border px-3 py-2">
          <div className="min-w-0">
            <Link to={`/p/${it.project_slug}`} className="font-medium hover:underline">{it.project_title}</Link>
            <div className="text-muted-foreground mt-0.5">
              关键词「{it.keyword}」 → {it.channel_target}
              {it.last_error && <span className="text-rose-500"> · {it.last_error}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Badge variant={
              it.status === "sent" ? "default" :
              it.status === "dropped" ? "secondary" : "destructive"
            }>{it.status === "sent" ? "已发送" : it.status === "dropped" ? "已丢弃" : it.status === "failed" ? "失败" : it.status}</Badge>
            {isAdmin && (it.status === "failed" || it.status === "dropped") && (
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => retry(it.id)} title="重试发送">
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
